import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Play, Pause, MessageCircle, Building2, Users, Settings, HelpCircle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import '../../css/components/FullChat/ProductTour.css';

const ProductTour = ({ isOpen, onClose, onComplete }) => {
  const { theme, currentThemeName } = useTheme();
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const tourRef = useRef(null);

  const tourSteps = [
    { id: 'welcome', title: t('profile.tour.welcome.title'), description: t('profile.tour.welcome.description'), icon: <HelpCircle size={24} />, highlight: null, action: null },
    { id: 'quick-options', title: t('profile.tour.quickOptions.title'), description: t('profile.tour.quickOptions.description'), icon: <MessageCircle size={24} />, highlight: '.quick-options-container', action: t('profile.tour.quickOptions.action') },
    { id: 'business-type', title: t('profile.tour.businessType.title'), description: t('profile.tour.businessType.description'), icon: <Building2 size={24} />, highlight: '.business-type-section', action: t('profile.tour.businessType.action') },
    { id: 'chat-interface', title: t('profile.tour.chatInterface.title'), description: t('profile.tour.chatInterface.description'), icon: <MessageCircle size={24} />, highlight: '.chat-messages', action: t('profile.tour.chatInterface.action') },
    { id: 'voice-controls', title: t('profile.tour.voiceControls.title'), description: t('profile.tour.voiceControls.description'), icon: <Play size={24} />, highlight: '.chat-controls', action: t('profile.tour.voiceControls.action') },
    { id: 'sidebar-menu', title: t('profile.tour.sidebarMenu.title'), description: t('profile.tour.sidebarMenu.description'), icon: <Settings size={24} />, highlight: '.sidebar-menu', action: t('profile.tour.sidebarMenu.action') },
    { id: 'completion', title: t('profile.tour.completion.title'), description: t('profile.tour.completion.description'), icon: <Users size={24} />, highlight: null, action: t('profile.tour.completion.action') }
  ];

  const totalSteps = tourSteps.length;
  const currentTourStep = tourSteps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      onComplete?.();
    }
  }, [currentStep, totalSteps, onComplete]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    onClose?.();
    onComplete?.();
  }, [onClose, onComplete]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) handleSkip();
  }, [handleSkip]);

  useEffect(() => {
    if (isOpen && tourRef.current) {
      tourRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isOpen, currentStep]);

  useEffect(() => {
    if (!isPlaying) return;
    const timer = setTimeout(() => {
      if (currentStep < totalSteps - 1) {
        setCurrentStep((prev) => prev + 1);
      } else {
        setIsPlaying(false);
        onComplete?.();
      }
    }, 3500);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, totalSteps, onComplete]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, handleSkip, handlePrevious, handleNext]);

  if (!isOpen) return null;

  const isDark = currentThemeName === 'dark';
  const isBeige = currentThemeName === 'beige';
  const modalBg = theme.modalBg ?? (isDark ? '#1e293b' : isBeige ? '#faf5eb' : '#ffffff');
  const borderColor = isDark ? 'rgba(59, 130, 246, 0.25)' : isBeige ? 'rgba(139, 69, 19, 0.2)' : (theme.primaryBorder || '#e2e8f0');
  const primaryText = theme.primaryText || (isDark ? '#f1f5f9' : '#1e293b');
  const secondaryText = theme.secondaryText || (isDark ? '#94a3b8' : '#64748b');

  const content = (
    <>
      {/* Backdrop */}
      <div
        className="tour-backdrop"
        onClick={handleBackdropClick}
        style={{
          position: 'fixed',
          inset: 0,
          background: isDark ? 'rgba(15, 23, 42, 0.8)' : 'rgba(15, 23, 42, 0.5)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: 9998,
          animation: 'tourBackdropFadeIn 0.3s ease-out'
        }}
      />

      {/* Modal - centered */}
      <div
        ref={tourRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        aria-describedby="tour-description"
        className="tour-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: modalBg,
          borderRadius: 24,
          padding: '28px 32px',
          maxWidth: 460,
          width: 'calc(100vw - 48px)',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: isDark ? '0 25px 50px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)' : '0 25px 50px -12px rgba(0,0,0,0.2)',
          border: `1px solid ${borderColor}`,
          zIndex: 9999,
          animation: 'tourSlideIn 0.4s ease-out'
        }}
      >
        {/* Header row: icon + title block + close */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 44,
                height: 44,
                background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
                borderRadius: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                flexShrink: 0
              }}
            >
              {currentTourStep.icon}
            </div>
            <div>
              <h2 id="tour-title" style={{ fontSize: '1.2rem', fontWeight: 700, color: primaryText, margin: 0, fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
                {currentTourStep.title}
              </h2>
              <p style={{ fontSize: '0.8rem', color: secondaryText, margin: '4px 0 0', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
                {t('profile.tour.step')} {currentStep + 1} {t('profile.tour.of')} {totalSteps}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSkip}
            aria-label={t('profile.tour.skip') || 'Skip tour'}
            style={{
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
              border: 'none',
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: secondaryText,
              cursor: 'pointer',
              transition: 'background 0.2s, color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
              e.currentTarget.style.color = primaryText;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
              e.currentTarget.style.color = secondaryText;
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', borderRadius: 2, marginBottom: 20, overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              width: `${((currentStep + 1) / totalSteps) * 100}%`,
              background: 'linear-gradient(90deg, #3b82f6, #22c55e)',
              borderRadius: 2,
              transition: 'width 0.35s ease'
            }}
          />
        </div>

        {/* Description */}
        <p id="tour-description" style={{ fontSize: '1rem', lineHeight: 1.65, color: secondaryText, marginBottom: 20, fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
          {currentTourStep.description}
        </p>

        {/* Action hint (optional) */}
        {currentTourStep.action && (
          <div
            style={{
              background: isDark ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)',
              border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.25)' : 'rgba(59, 130, 246, 0.2)'}`,
              borderRadius: 12,
              padding: '12px 16px',
              marginBottom: 20,
              fontSize: '0.9rem',
              color: theme.primary || '#3b82f6',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
          >
            💡 {currentTourStep.action}
          </div>
        )}

        {/* Step dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24 }}>
          {tourSteps.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => { setCurrentStep(index); setIsPlaying(false); }}
              aria-label={`${t('profile.tour.step')} ${index + 1}`}
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                border: 'none',
                background: index === currentStep ? (theme.primary || '#3b82f6') : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'),
                cursor: 'pointer',
                transition: 'transform 0.2s, background 0.2s'
              }}
              onMouseEnter={(e) => {
                if (index !== currentStep) e.currentTarget.style.transform = 'scale(1.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            />
          ))}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            onClick={handlePrevious}
            disabled={isFirst}
            style={{
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
              border: `1px solid ${borderColor}`,
              borderRadius: 12,
              padding: '10px 14px',
              fontSize: 14,
              fontWeight: 500,
              cursor: isFirst ? 'not-allowed' : 'pointer',
              color: isFirst ? secondaryText : primaryText,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              opacity: isFirst ? 0.5 : 1,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
          >
            <ChevronLeft size={18} />
            {t('profile.tour.previous')}
          </button>

          <button
            type="button"
            onClick={() => setIsPlaying(!isPlaying)}
            style={{
              background: isPlaying ? (theme.error || '#ef4444') : (theme.primary || '#3b82f6'),
              color: 'white',
              border: 'none',
              borderRadius: 12,
              padding: '10px 14px',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
          >
            {isPlaying ? <Pause size={18} /> : <Play size={18} />}
            {isPlaying ? t('profile.tour.pause') : t('profile.tour.play')}
          </button>

          <button
            type="button"
            onClick={isLast ? handleSkip : handleNext}
            style={{
              background: theme.primary || '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: 12,
              padding: '10px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.35)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {isLast ? (t('profile.tour.close') || 'Close') : t('profile.tour.next')}
            {!isLast && <ChevronRight size={18} />}
          </button>
        </div>
      </div>

      {/* Optional highlight overlay (visual only) */}
      {currentTourStep.highlight && (
        <div
          className="tour-highlight"
          style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 9997
          }}
        />
      )}
    </>
  );

  return createPortal(content, document.body);
};

export default ProductTour;
